import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Chip, CircularProgress, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

import { GET_ROUTE_AUTOMATIC_COUPON_SETTING_FORMS } from '../../../core/constants';
import { getAutomaticCouponSettingsState } from '../../../features/coupon/get_automatic_coupon_settings';
import { getAutomaticCouponType } from '../../../core/enums/price/automatic_coupon_setting';
import { updateAutomaticCouponSettingStatusNotificationData } from '../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../core/enums/common/api';
import { updateAutomaticCouponSettingStatusAction } from '../../../features/coupon/update_automatic_coupon_setting_status';
import useNotification from '../../../core/hooks/use_notification';

const AutomaticCouponSettingList = ({ refreshAutomaticCouponSettingList }) => {
    const { showNotification } = useNotification();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const getAutomaticCouponSettings = useSelector(getAutomaticCouponSettingsState);

    const [updatingSettingIds, setUpdatingSettingIds] = useState({});

    const onUpdateAutomaticCouponSettingStatus = (automaticCouponType) => {
        setUpdatingSettingIds(prev => ({ ...prev, [automaticCouponType]: true }));

        dispatch(updateAutomaticCouponSettingStatusAction(automaticCouponType))
            .then(response => {
                showNotification(updateAutomaticCouponSettingStatusNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshAutomaticCouponSettingList();
            })
            .finally(() => {
                setUpdatingSettingIds(prev => ({ ...prev, [automaticCouponType]: false }));
            });
    };

    return (
        <TableContainer component={ Paper } variant="outlined">
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                        <TableCell>Typ kuponu</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="right" sx={ { pr: 3 } }></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        getAutomaticCouponSettings?.data?.map(automaticCouponSetting => {
                            const isUpdating = updatingSettingIds[automaticCouponSetting?.automaticCouponType] || false;

                            return (
                                <TableRow key={ automaticCouponSetting?.automaticCouponType } hover>
                                    <TableCell sx={ { fontWeight: 500 } }>
                                        { getAutomaticCouponType(automaticCouponSetting?.automaticCouponType)?.message }
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={ automaticCouponSetting?.active ? 'Aktywny' : 'Nieaktywny' }
                                            color={ automaticCouponSetting?.active ? 'success' : 'default' }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right" sx={ { pr: 2 } }>
                                        <Stack direction="row" spacing={ 1 } justifyContent="flex-end"
                                               alignItems="center">
                                            <Tooltip
                                                title={ automaticCouponSetting?.active ? 'Dezaktywuj' : 'Aktywuj' }>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color={ automaticCouponSetting?.active ? 'error' : 'success' }
                                                        onClick={ () => onUpdateAutomaticCouponSettingStatus(automaticCouponSetting?.automaticCouponType) }
                                                        disabled={ isUpdating }
                                                    >
                                                        { isUpdating ? <CircularProgress size={ 20 } color="inherit"/> :
                                                            <PowerSettingsNewIcon fontSize="small"/> }
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Edytuj reguły">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={ () => navigate(GET_ROUTE_AUTOMATIC_COUPON_SETTING_FORMS(automaticCouponSetting?.automaticCouponType)) }
                                                >
                                                    <EditIcon fontSize="small"/>
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default AutomaticCouponSettingList;